import React from 'react';
import { useApp } from '../context/AppContext';
import { MOCK_PRODUCTS, DOOR_TYPES } from '../data/mockData';
import { 
  Building2, 
  Layers, 
  DoorOpen, 
  Package, 
  Wand2,
  BarChart3
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export function BuildingDetail({ building }) {
  const { actions } = useApp();

  // Calculate statistics
  const totalFloors = building.floors.length;
  const totalDoors = building.floors.reduce((sum, f) => sum + f.doors.length, 0);
  const totalProducts = building.floors.reduce((sum, f) => 
    sum + f.doors.reduce((doorSum, d) => doorSum + d.products.length, 0), 0
  );

  // Door type breakdown
  const doorsByType = building.floors.reduce((acc, floor) => {
    floor.doors.forEach(door => {
      acc[door.type] = (acc[door.type] || 0) + 1;
    });
    return acc;
  }, {});

  // Calculate total cost
  const totalCost = building.floors.reduce((sum, floor) => {
    return sum + floor.doors.reduce((floorSum, door) => {
      return floorSum + door.products.reduce((doorSum, productId) => {
        const product = MOCK_PRODUCTS.find(p => p.id === productId);
        return doorSum + (product?.price || 0);
      }, 0);
    }, 0);
  }, 0);

  const handleAutoAssign = () => {
    actions.autoAssignHardware('building', building.id);
    toast.success('Hardware auto-assigned to all doors in this building');
  };

  const doorTypeColors = {
    'standard': 'bg-slate-100 text-slate-700 border-slate-200',
    'exit': 'bg-green-100 text-green-700 border-green-200',
    'fire-rated': 'bg-red-100 text-red-700 border-red-200'
  };

  return (
    <div className="space-y-6" data-testid="building-detail">
      {/* Building Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Building2 className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{building.name}</h3>
            <p className="text-sm text-muted-foreground">
              {totalFloors} floors • {totalDoors} doors
            </p>
          </div>
        </div>
        <Button onClick={handleAutoAssign} data-testid="building-auto-assign-btn">
          <Wand2 className="w-4 h-4 mr-2" />
          Auto Assign All Hardware
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard 
          label="Floors"
          value={totalFloors}
          icon={<Layers className="w-4 h-4" />}
        />
        <StatCard 
          label="Total Doors"
          value={totalDoors}
          icon={<DoorOpen className="w-4 h-4" />}
        />
        <StatCard 
          label="Products Assigned"
          value={totalProducts}
          icon={<Package className="w-4 h-4" />}
        />
        <StatCard 
          label="Total Value"
          value={`$${totalCost.toFixed(0)}`}
          icon={<BarChart3 className="w-4 h-4" />}
          highlight
        />
      </div>

      {/* Door Type Breakdown */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Door Types
        </h4>
        <div className="flex gap-2">
          {DOOR_TYPES.map(type => (
            <Badge 
              key={type.id}
              variant="outline"
              className={cn(doorTypeColors[type.id])}
            >
              {type.name}: {doorsByType[type.id] || 0}
            </Badge>
          ))}
        </div>
      </div>

      {/* Floor Breakdown */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Floor Breakdown
        </h4>
        
        {building.floors.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-6 text-center">
            <Layers className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">No floors in this building</p>
          </div>
        ) : (
          <div className="border border-border rounded-lg divide-y divide-border">
            {building.floors.map((floor) => {
              const floorDoors = floor.doors.length;
              const floorProducts = floor.doors.reduce((sum, d) => sum + d.products.length, 0);
              const floorCost = floor.doors.reduce((sum, door) => {
                return sum + door.products.reduce((doorSum, productId) => {
                  const product = MOCK_PRODUCTS.find(p => p.id === productId);
                  return doorSum + (product?.price || 0);
                }, 0);
              }, 0);

              return (
                <div 
                  key={floor.id}
                  className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => actions.selectNode({ 
                    type: 'floor', 
                    id: floor.id, 
                    path: { buildingId: building.id, floorId: floor.id }
                  })}
                  data-testid={`building-floor-row-${floor.id}`}
                >
                  <Layers className="w-4 h-4 text-amber-600 shrink-0" />
                  <span className="flex-1 text-sm font-medium">{floor.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {floorDoors} doors
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {floorProducts} products
                  </span>
                  <span className="text-sm font-medium w-20 text-right">
                    ${floorCost.toFixed(0)}
                  </span>
                </div>
              );
            })}
            
            {/* Total Row */}
            <div className="flex items-center gap-3 p-3 bg-muted/50">
              <Building2 className="w-4 h-4 text-primary shrink-0" />
              <span className="flex-1 text-sm font-medium">Total</span>
              <span className="text-xs text-muted-foreground">
                {totalDoors} doors
              </span>
              <span className="text-xs text-muted-foreground">
                {totalProducts} products
              </span>
              <span className="text-sm font-semibold w-20 text-right">
                ${totalCost.toFixed(0)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, highlight = false }) {
  return (
    <div className={cn(
      "border rounded-lg p-3",
      highlight ? "border-primary bg-primary/5" : "border-border"
    )}>
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <p className={cn("text-xl font-semibold", highlight && "text-primary")}>{value}</p>
    </div>
  );
}
